import React, { useMemo } from 'react';
import { Grid, Box, Typography } from '@mui/material';
import { AccountBalance, TrendingUp, Savings } from '@mui/icons-material';
import { useIntl } from 'react-intl';
import StatCard from './StatCard';
import { BankAccount } from '../types/BankAccount';
import { Investment } from '../types/Investment';
import { designTokens } from '../theme/designTokens';
import { FinancialSummarySkeleton } from './LoadingSkeleton';

interface FinancialSummaryProps {
    bankAccounts: BankAccount[];
    investments: Investment[];
    currentPrices: { [key: string]: { price: number; price_change_percentage_24h: number } };
    isLoading: boolean;
    showValues?: boolean;
    averageMonthlyExpenses?: number;
    netWorthHistory?: number[];
}

const FinancialSummary: React.FC<FinancialSummaryProps> = ({
    bankAccounts,
    investments,
    currentPrices,
    isLoading,
    showValues = true,
    averageMonthlyExpenses = 0,
    netWorthHistory = []
}) => {
    const intl = useIntl();

    const { totalBankBalance, totalInvestments, totalAssets, investmentPnL24h, investmentPnLPercent, specificBanksTotal } = useMemo(() => {
        const bankTotal = bankAccounts.reduce((sum, account) => sum + account.balance, 0);

        const targets = ['revolut', 'santander', 'bankinter', 'edenred'];
        const specificTotal = bankAccounts
            .filter(acc => targets.includes(acc.name.toLowerCase()))
            .reduce((sum, acc) => sum + acc.balance, 0);

        let investmentsTotal = 0;
        let pnlTotal = 0;

        investments.forEach(investment => {
            if (!investment.token) return;
            const priceData = currentPrices[investment.token.toUpperCase()];
            const currentPrice = priceData ? priceData.price : 0;
            const priceChange24h = priceData ? priceData.price_change_percentage_24h : 0;

            const value = currentPrice * Number(investment.quantity);
            investmentsTotal += value;

            // Calculate 24h Profit/Loss amount
            // Formula: Value - (Value / (1 + change/100))
            // Only strictly correct if quantity didn't change, but good enough approximation for analytics
            const valueYesterday = value / (1 + (priceChange24h / 100));
            pnlTotal += (value - valueYesterday);
        });

        const pnlPercent = investmentsTotal > 0 ? (pnlTotal / (investmentsTotal - pnlTotal)) * 100 : 0;

        return {
            totalBankBalance: bankTotal,
            totalInvestments: investmentsTotal,
            totalAssets: bankTotal + investmentsTotal,
            investmentPnL24h: pnlTotal,
            investmentPnLPercent: pnlPercent,
            specificBanksTotal: specificTotal
        };
    }, [bankAccounts, investments, currentPrices]);

    if (isLoading) {
        return <FinancialSummarySkeleton />;
    }

    const liquidityRatio = averageMonthlyExpenses > 0 ? totalBankBalance / averageMonthlyExpenses : 0;

    return (
        <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={4}>
                <StatCard
                    title={intl.formatMessage({ id: 'totalAssets', defaultMessage: 'Total Assets' })}
                    value={`€${Math.round(totalAssets).toLocaleString()}`}
                    icon={<Savings />}
                    gradient={designTokens.colors.primary.gradient} // Dark Slate
                    showValues={showValues}
                    sparklineData={netWorthHistory}
                    subtitle={intl.formatMessage(
                        { id: 'netWorthHistory', defaultMessage: 'Net Worth (6 Months)' }
                    )}
                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <StatCard
                    title={intl.formatMessage({ id: 'bankBalance', defaultMessage: 'Bank Balance' })}
                    value={`€${Math.round(totalBankBalance).toLocaleString()}`}
                    icon={<AccountBalance />}
                    gradient="linear-gradient(135deg, #10b981 0%, #059669 100%)" // Emerald (Success) for Liquid
                    showValues={showValues}
                    subtitle={liquidityRatio > 0 ?
                        intl.formatMessage(
                            { id: 'liquidityRatio', defaultMessage: 'Runway: {months} Months' },
                            { months: liquidityRatio.toFixed(1) }
                        ) : undefined
                    }
                    extraLine={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                            <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: designTokens.typography.fontWeight.medium }}>
                                {intl.formatMessage({ id: 'specificBanksTotal', defaultMessage: 'Total (Rev, Sant, Bank, Eden)' })}
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: designTokens.typography.fontWeight.bold }}>
                                {showValues ? `€${Math.round(specificBanksTotal).toLocaleString()}` : '••••'}
                            </Typography>
                        </Box>
                    }
                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <StatCard
                    title={intl.formatMessage({ id: 'investments', defaultMessage: 'Investments' })}
                    value={`€${Math.round(totalInvestments).toLocaleString()}`}
                    icon={<TrendingUp />}
                    gradient="linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)" // Violet/Purple for Investments
                    showValues={showValues}
                    secondaryTrend={{
                        value: showValues ?
                            `${investmentPnL24h >= 0 ? '+' : ''}€${Math.abs(investmentPnL24h).toFixed(2)}` :
                            '••••',
                        label: intl.formatMessage({ id: '24h', defaultMessage: '(24h)' }),
                        isPositive: investmentPnL24h >= 0
                    }}
                    trend={{
                        value: investmentPnLPercent,
                        isPositive: investmentPnLPercent >= 0,
                        label: "24h %"
                    }}
                />
            </Grid>
        </Grid>
    );
};

export default FinancialSummary;
